import type { ContentBlock, TextContentBlock, VisualizationContentBlock, InteractiveElementsContentBlock, ChatMessageData, MessageStatus } from './types'
import type { InteractiveElement } from '@/types/botArtifacts'

/**
 * Detect whether a string contains Markdown formatting.
 * Used to decide between prose rendering and plain text.
 */
export function containsMarkdown(content: string): boolean {
  if (!content || typeof content !== 'string') return false

  const markdownPatterns = [
    /^#+\s.+$/m,              // Headers
    /^\s*[-*+]\s.+$/m,       // Unordered lists
    /^\s*\d+\.\s.+$/m,       // Ordered lists
    /^\s*>\s.+$/m,           // Blockquotes
    /^\s*```[\s\S]*?```\s*$/m, // Code blocks
    /\|.+\|.+\|/m,           // Tables
    /^\s*---+\s*$/m,         // Horizontal rules
    /\[.+?\]\(.+?\)/,        // Links
  ]

  // Table detection
  if (content.includes('|') && /\|[^|]+\|[^|]+\|/.test(content)) {
    return true
  }

  // Header detection
  if (content.includes('##') || /^#\s.+$/m.test(content)) {
    return true
  }

  return markdownPatterns.some(pattern => pattern.test(content))
}

/**
 * Check if a parsed JSON object looks like an SVS visualization payload.
 */
function isSVSPayload(obj: unknown): obj is Record<string, unknown> {
  if (!obj || typeof obj !== 'object') return false
  const record = obj as Record<string, unknown>
  return (
    typeof record.version === 'string' &&
    typeof record.type === 'string' &&
    record.data !== undefined
  )
}

/**
 * Parse raw message content into an array of ContentBlocks.
 *
 * Scans for fenced JSON code blocks that contain valid SVS payloads
 * and extracts them as VisualizationBlocks. All other content is
 * emitted as TextBlocks. This handles the common case where the bot
 * sends SVS JSON inline within a markdown response.
 */
export function parseContentToBlocks(content: string): ContentBlock[] {
  if (!content || !content.trim()) return []

  const blocks: ContentBlock[] = []

  // Match fenced code blocks: ```json ... ``` or ``` ... ```
  // Using a regex that captures the content between fences
  const fencePattern = /```(?:json)?\s*\n([\s\S]*?)```/g
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = fencePattern.exec(content)) !== null) {
    const codeContent = match[1].trim()

    // Try to parse as JSON and check if it's an SVS payload
    let parsed: unknown = null
    try {
      parsed = JSON.parse(codeContent)
    } catch {
      // Not valid JSON — leave as text
    }

    if (parsed && isSVSPayload(parsed)) {
      // Emit any text before this code block
      const textBefore = content.slice(lastIndex, match.index).trim()
      if (textBefore) {
        blocks.push({ type: 'text', content: textBefore } as TextContentBlock)
      }

      // Emit the visualization block
      const vizBlock: VisualizationContentBlock = {
        type: 'visualization',
        payload: parsed as Record<string, unknown>,
        textFallback: typeof parsed.textFallback === 'string' ? parsed.textFallback : undefined,
      }
      blocks.push(vizBlock)

      lastIndex = match.index + match[0].length
    }
    // If it's not SVS JSON, skip it — it stays as part of the text
  }

  // Emit any remaining text after the last visualization
  const remaining = content.slice(lastIndex).trim()
  if (remaining) {
    blocks.push({ type: 'text', content: remaining } as TextContentBlock)
  }

  // If no visualizations were found, return the whole thing as text
  if (blocks.length === 0) {
    blocks.push({ type: 'text', content } as TextContentBlock)
  }

  return blocks
}

/**
 * Build a ChatMessageData from the raw fields used by both
 * copilot.tsx and analytics-chat.tsx.
 */
export function buildChatMessage(opts: {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp?: string
  status?: MessageStatus
  error?: ChatMessageData['error']
  progress?: ChatMessageData['progress']
  toolExecutions?: ChatMessageData['toolExecutions']
  thinkingContent?: string
  thinkingDuration?: number
  isActionResult?: boolean
  metadata?: Record<string, unknown>
  visualization?: Record<string, unknown>
  interactiveElements?: InteractiveElement[]
}): ChatMessageData {
  const blocks: ContentBlock[] = parseContentToBlocks(opts.content)

  // Append structured visualization (from BotMessage.visualization field)
  if (opts.visualization && isSVSPayload(opts.visualization)) {
    const vizBlock: VisualizationContentBlock = {
      type: 'visualization',
      payload: opts.visualization,
      textFallback: typeof (opts.visualization as Record<string, unknown>).textFallback === 'string'
        ? (opts.visualization as Record<string, unknown>).textFallback as string
        : undefined,
    }
    blocks.push(vizBlock)
  }

  // Append interactive elements (from BotMessage.interactiveElements field)
  if (opts.interactiveElements && opts.interactiveElements.length > 0) {
    const ieBlock: InteractiveElementsContentBlock = {
      type: 'interactive-elements',
      elements: opts.interactiveElements,
    }
    blocks.push(ieBlock)
  }

  return {
    id: opts.id,
    role: opts.role,
    blocks,
    rawContent: opts.content,
    timestamp: opts.timestamp,
    status: opts.status,
    error: opts.error,
    progress: opts.progress,
    toolExecutions: opts.toolExecutions,
    thinkingContent: opts.thinkingContent,
    thinkingDuration: opts.thinkingDuration,
    isActionResult: opts.isActionResult,
    metadata: opts.metadata,
    interactiveElements: opts.interactiveElements,
    visualization: opts.visualization,
  }
}

/**
 * Incrementally update an existing ChatMessageData with new content from a
 * BotMessage chunk. Unlike buildChatMessage (which rebuilds blocks from
 * scratch), this preserves the chronological ordering of text,
 * visualizations, and interactive elements in the blocks array.
 */
export function appendToMessage(
  existing: ChatMessageData,
  delta: {
    content?: string | null
    visualization?: Record<string, unknown>
    interactiveElements?: InteractiveElement[]
  },
  overrides?: {
    status?: MessageStatus
    thinkingContent?: string
  },
): ChatMessageData {
  const blocks = [...existing.blocks]
  const rawContent = existing.rawContent + (delta.content || '')

  // Append new text content to the last text block or create a new one
  if (delta.content) {
    const lastBlock = blocks[blocks.length - 1]
    if (lastBlock && lastBlock.type === 'text') {
      blocks[blocks.length - 1] = {
        type: 'text',
        content: (lastBlock as TextContentBlock).content + delta.content,
      }
    } else {
      blocks.push({ type: 'text', content: delta.content } as TextContentBlock)
    }
  }

  // Append visualization at current chronological position
  if (delta.visualization && isSVSPayload(delta.visualization)) {
    blocks.push({
      type: 'visualization',
      payload: delta.visualization,
      textFallback: typeof (delta.visualization as Record<string, unknown>).textFallback === 'string'
        ? (delta.visualization as Record<string, unknown>).textFallback as string
        : undefined,
    } as VisualizationContentBlock)
  }

  // Append interactive elements at current position
  if (delta.interactiveElements && delta.interactiveElements.length > 0) {
    blocks.push({
      type: 'interactive-elements',
      elements: delta.interactiveElements,
    } as InteractiveElementsContentBlock)
  }

  return {
    ...existing,
    blocks,
    rawContent,
    status: overrides?.status ?? existing.status,
    thinkingContent: overrides?.thinkingContent !== undefined ? overrides.thinkingContent : existing.thinkingContent,
    visualization: delta.visualization || existing.visualization,
    interactiveElements: delta.interactiveElements || existing.interactiveElements,
  }
}

export function formatDuration(ms: number): string {
  const s = Math.round(ms / 1000)
  if (s < 60) return `${s} second${s !== 1 ? 's' : ''}`
  const m = Math.floor(s / 60)
  return `${m} minute${m !== 1 ? 's' : ''}`
}

export function generateMessageId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}
